import { useEffect, useState } from "react";
import styled from "styled-components";
import { styled as muiStyled } from "@mui/material/styles";
import { useSnackbar } from "notistack";

import { Grid, Stack } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";

import { updateQuotationPdf } from "src/api/purchaseOrder";

import font from "src/styles/font";
import device from "src/styles/device";
import { checkNotEmpty } from "src/common";
import { Black, Primary } from "src/styles/color";

import Dialog from "src/components/core/Dialog";
import Button from "src/components/core/Button";
import TextField from "src/components/core/TextField";
import UploadPdf from "src/components/upload/dropzone/UploadPdf";

const CloseButton = styled(IconButton)`
  &.MuiIconButton-root {
    position: absolute;
    top: 16px;
    right: 16px;
    color: ${Black};
  }
`;

const WrapperTextField = styled(Stack)`
  width: 100%;
`;

const TextFieldLabel = styled.p`
  margin: 0;
  font-size: ${font.size.sm};
  font-weight: 400;
  line-height: 24px;

  @media ${device.mobileL} {
    font-size: ${font.size.xs};
    line-height: 16px;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-adornedEnd {
    padding-right: 2px;
  }
`;

const StyledDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 16,
  },
  "& .MuiDialogContent-root": {
    maxHeight: "500px",
    border: 0,
    padding: 24,
    paddingTop: 0,
  },
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: 640,
    },
  },
  "& .MuiDialogActions-root": {
    justifyContent: "center",
  },
  "& .MuiDialogActions-root>:not(:first-of-type)": {
    marginLeft: 24,
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiPaper-root": {
      margin: 24,
    },
    "& .MuiDialogContent-root": {
      padding: 16,
      paddingTop: 0,
    },
    "& .MuiDialogActions-root": {
      padding: 16,
    },
  },
}));

const EditDocumentDialog = ({ orderId, quoData, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [updateOrder, setUpdateOrder] = useState({
    qtNumber: "",
    quotationPdf: null,
    // quotationOtherPdf: null,
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!quoData) return;

    setUpdateOrder({
      ...quoData,
    });
  }, [quoData]);

  const onSubmit = async () => {
    try {
      setIsLoading(true);
      const updateParams = {
        qtNumber: updateOrder?.qtNumber,
        ...(!!updateOrder?.quotationPdf?._id
          ? { quotationPdf: updateOrder?.quotationPdf?._id }
          : { quotationPdf: updateOrder?.quotationPdf }),
        ...(!!updateOrder?.quotationOtherPdf
          ? !!updateOrder?.quotationOtherPdf?._id
            ? { quotationOtherPdf: updateOrder?.quotationOtherPdf?._id }
            : { quotationOtherPdf: updateOrder?.quotationOtherPdf }
          : {}),
      };

      await updateQuotationPdf(orderId, updateParams);
      enqueueSnackbar(`Update Quotation Success!`, {
        variant: "success",
      });
      await onSuccess();
    } catch (err) {
      console.error("err:", err.response);
      enqueueSnackbar(err.response.data.message, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = ["qtNumber", "quotationPdf"].every((required) => {
    let objVal = updateOrder[required];
    return checkNotEmpty(objVal);
  });

  return (
    <StyledDialog
      onClose={onClose}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <DialogTitle
        sx={{
          p: {
            sm: 3,
            xs: 2,
          },
          display: "flex",
          alignItems: "center",
          gap: 1,
          fontSize: {
            sm: font.size.lg,
            xs: font.size.md,
          },
          fontWeight: 600,
        }}
      >
        แก้ไขเอกสาร
        <CloseButton aria-label="close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </CloseButton>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>QT Number</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  setUpdateOrder({
                    ...updateOrder,
                    qtNumber: e.target.value,
                  });
                }}
                value={updateOrder?.qtNumber}
                error={!checkNotEmpty(updateOrder?.qtNumber)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>ใบเสนอราคา (สำหรับลูกค้า)</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <>
                      {checkNotEmpty(updateOrder?.quotationPdf) ? (
                        <IconButton
                          // sx={{ padding: 0 }}
                          onClick={() => {
                            setUpdateOrder({
                              ...updateOrder,
                              quotationPdf: null,
                            });
                          }}
                        >
                          <DeleteOutlineIcon
                            sx={{
                              color: Primary,
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      ) : (
                        <UploadPdf
                          callBackFile={(fileCallBlack) => {
                            setUpdateOrder({
                              ...updateOrder,
                              quotationPdf: fileCallBlack,
                            });
                          }}
                          closeAlert={false}
                        >
                          <IconButton>
                            <AttachFileIcon
                              sx={{
                                color: Black,
                                cursor: "pointer",
                                fontSize: font.size.lg,
                                ":hover": {
                                  color: "#e04132",
                                },
                              }}
                            />
                          </IconButton>
                        </UploadPdf>
                      )}
                    </>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                // onChange={(e) => {
                //   setUpdateOrder({
                //     ...updateOrder,
                //     note: e.target.value,
                //   });
                // }}
                value={
                  !!updateOrder?.quotationPdf
                    ? !!updateOrder?.quotationPdf?._id
                      ? updateOrder?.quotationPdf?.originalFileName
                      : updateOrder?.quotationPdf?.name
                    : ""
                }
                error={!checkNotEmpty(updateOrder?.quotationPdf)}
              />
            </WrapperTextField>
          </Grid>

          <Grid item xs={12}>
            <WrapperTextField gap={0.5}>
              <TextFieldLabel>เอกสารเพิ่มเติม</TextFieldLabel>
              <StyledTextField
                variant="outlined"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <>
                      {checkNotEmpty(updateOrder?.quotationOtherPdf) ? (
                        <IconButton
                          // sx={{ padding: 0 }}
                          onClick={() => {
                            setUpdateOrder({
                              ...updateOrder,
                              quotationOtherPdf: null,
                            });
                          }}
                        >
                          <DeleteOutlineIcon
                            sx={{
                              color: Primary,
                              cursor: "pointer",
                            }}
                          />
                        </IconButton>
                      ) : (
                        <UploadPdf
                          callBackFile={(fileCallBlack) => {
                            setUpdateOrder({
                              ...updateOrder,
                              quotationOtherPdf: fileCallBlack,
                            });
                          }}
                          closeAlert={false}
                        >
                          <IconButton>
                            <AttachFileIcon
                              sx={{
                                color: Black,
                                cursor: "pointer",
                                fontSize: font.size.lg,
                                ":hover": {
                                  color: "#e04132",
                                },
                              }}
                            />
                          </IconButton>
                        </UploadPdf>
                      )}
                    </>
                  ),
                }}
                InputLabelProps={{ shrink: true }}
                value={
                  updateOrder?.quotationOtherPdf?._id
                    ? updateOrder?.quotationOtherPdf?.originalFileName
                    : updateOrder?.quotationOtherPdf?.name || ""
                }
              />
            </WrapperTextField>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={() => onClose()}
          fullWidth
          sx={{
            maxWidth: 180,
            borderRadius: 4,
          }}
        >
          ยกเลิก
        </Button>

        <Button
          variant="contained"
          size="large"
          onClick={() => onSubmit()}
          disabled={!isFormValid || isLoading}
          fullWidth
          sx={{
            maxWidth: 180,
            borderRadius: 4,
          }}
        >
          ยืนยัน
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default EditDocumentDialog;
